import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import '../../App.css';

const ChatContainer = styled.div`
     border: 1px solid #ccc;
     padding: 10px;
     margin-top: 10px;
     max-height: 300px;
     overflow-y: auto;
     background-color: #f9f9f9;
     border-radius: 8px;
     box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	 display: flex;
     flex-direction: column;
   `;

   const Message = styled.div`
     margin-bottom: 8px;
     padding: 8px;
     border-radius: 8px;
     background-color: ${(props) => (props.sender === 'user' ? '#e2f0cb' : '#ffffff')};
     align-self: ${(props) => (props.sender === 'user' ? 'flex-end' : 'flex-start')};
     box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
     max-width: 80%;
	 white-space: pre-wrap;
   `;

   const Sender = styled.strong`
     display: block;
     margin-bottom: 4px;
     color: #555;
     font-size: 0.8em;
   `;

   const InputContainer = styled.div`
     display: flex;
     margin-top: 10px;
   `;

   const Input = styled.input`
     flex: 1;
     padding: 8px;
     border: 1px solid #ddd;
     border-radius: 4px;
     margin-right: 8px;
   `;

   const SendButton = styled.button`
     padding: 8px 12px;
     background-color: #007bff;
     color: white;
     border: none;
     border-radius: 4px;
     cursor: pointer;
     transition: background-color 0.2s;

     &:hover {
       background-color: #0056b3;
     }

     &:disabled {
       background-color: #cccccc;
       cursor: not-allowed;
     }
   `;

   const ChatTitle = styled.h3`
     font-size: 1.2em;
     margin-bottom: 10px;
     color: #333;
     text-align: center;
   `;

 function AssistantChat({ onSendMessage, isLoadingAI, aiError }) {
   const [messages, setMessages] = useState([]);
   const [input, setInput] = useState('');
   const chatEndRef = useRef(null);

   useEffect(() => {
     chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
   }, [messages, isLoadingAI, aiError]);

    const handleSendMessage = async () => {
        if (!input.trim()) return; // Prevent sending empty messages

        const userMessage = input.trim();
        // Add user's message to chat immediately
        setMessages((prevMessages) => [...prevMessages, { text: userMessage, sender: 'user' }]);
        setInput(''); // Clear input field

        try {
            // Call the parent's handler (DesignCanvas's handleAssistantMessage)
            // This function is expected to return the AI's textual response
            const aiResponseText = await onSendMessage(userMessage);

            // Add AI's response to chat
            setMessages((prevMessages) => [
                ...prevMessages,
                { text: aiResponseText, sender: 'ai' },
            ]);
        } catch (error) {
            // Error handling for the chat UI, distinct from design errors
            console.error('Error receiving AI response from parent handler:', error);
            setMessages((prevMessages) => [
                ...prevMessages,
                { text: 'An unexpected error occurred. Please try again.', sender: 'ai' },
            ]);
        }
    };

    return (
        <div className="assistant-chat">
            <ChatTitle>AI Assistant</ChatTitle>
            <ChatContainer className="chat-messages">
                {messages.map((message, index) => (
                    <Message key={index} sender={message.sender}>
                        <Sender>{message.sender === 'user' ? 'You' : 'AI'}</Sender>
                        {message.text}
                    </Message>
                ))}
                {/* Display loading indicator and error message passed from parent */}
                {isLoadingAI && <div className="chat-loading">AI is thinking...</div>}
                {aiError && <div className="chat-error">{aiError}</div>}
                <div ref={chatEndRef} />
            </ChatContainer>
            <InputContainer className="chat-input-container">
                <Input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    className="chat-input"
                    placeholder="Ask me anything about this design..."
                    disabled={isLoadingAI} // Disable input while AI is thinking
                />
                <SendButton
                    onClick={handleSendMessage}
                    disabled={isLoadingAI || !input.trim()} // Disable button while loading or input is empty
                    className="chat-send-button"
                >
                    {isLoadingAI ? 'Thinking...' : 'Send'}
                </SendButton>
            </InputContainer>
        </div>
    );
}

export default AssistantChat;